import {
  Dropdown,
  Field,
  Input,
  makeStyles,
  mergeClasses,
  tokens,
  Option,
  Button,
  Text,
  RadioGroup,
  Radio,
} from "@fluentui/react-components";
import React, { useEffect } from "react";
import { AddFilled } from "@fluentui/react-icons";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import {
  ISignForm,
  SignColor,
  SignColorCode,
  Reflex,
} from "../../interfaces/ISignForm";
import { ITextFieldErrorText } from "../../interfaces/ITextFieldErrorText";
import {
  addTextRow,
  availableTextWidth,
  calculateScaledDimensions,
  canAddTextRow,
  fitsInAvailableTextHeight,
  fontColorOptions,
  fontSizeOptions,
  measureTextWidth,
  onSignExpand,
  updateRowNumber,
  textFieldHasErrorMessage,
} from "../../helper/signGeneratorHelper";
import { useTranslation } from "react-i18next";
import { sizeOptions } from "../../helper/signGeneratorHelper";
import { GridItem } from "../common/GridItem";
type SignTextFormProps = {
  signForm: UseFormReturn<ISignForm, any, ISignForm>;
  fieldsWithErrorMessage: ITextFieldErrorText[];
  setFieldsWithErrorMessage: React.Dispatch<
    React.SetStateAction<ITextFieldErrorText[]>
  >;
};
const useStyles = makeStyles({
  flexCenter: {
    display: "flex",
    alignItems: "center",
  },
  flexEnd: {
    display: "flex",
    justifyContent: "flex-end",
  },
  btnMargin: {
    marginTop: tokens.spacingVerticalXXL,
  },
  label: {
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase500,
    marginTop: tokens.spacingVerticalXXL,
  },
  field: {
    width: "100%",
    fontWeight: tokens.fontWeightBold,
    marginTop: tokens.spacingVerticalXXL,
  },
  widthHundred: {
    width: "100%",
  },
  widthInputMargin: {
    marginRight: "52px",
  },
  inputHeight: {
    height: tokens.spacingVerticalXXXL,
  },
  button: {
    marginTop: tokens.spacingVerticalXXL,
    fontWeight: tokens.fontWeightBold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorBrandForeground1,
  },
  buttonColor: {
    color: tokens.colorBrandForeground1,
  },
  icon: {
    marginRight: tokens.spacingHorizontalS,
  },
  font: {
    fontFamily: "Tratex, Arial, sans-serif !important",
  },
  errorText: {
    color: "#BF2828",
  },
  breakRow: {
    gridColumnStart: "1",
  },
  marginTopInModal: {
    marginTop: tokens.spacingVerticalL,
  },
});

export const SignTextForm = ({
  signForm,
  fieldsWithErrorMessage,
  setFieldsWithErrorMessage,
}: SignTextFormProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const fontSizeMenuOptions = fontSizeOptions();
  const colorOptions = fontColorOptions();
  const signSizeOptions = sizeOptions();
  const { control, watch, getValues, setValue } = signForm;
  const { primaryColor, height, width, symbol } = watch();
  const sign = watch();
  const { fields, append, remove, update } = useFieldArray({
    control: control,
    name: "texts",
  });

  useEffect(() => {
    fields.forEach((field, index) => {
      const isValidColorForYellow =
        field.color === SignColorCode.BLACK || field.color === SignColorCode.RED;
      const isValidColorForBlue =
        field.color === SignColorCode.WHITE || field.color === SignColorCode.RED;

      const newColor =
        primaryColor === SignColor.YELLOW
          ? isValidColorForYellow
            ? field.color
            : SignColorCode.BLACK
          : isValidColorForBlue
            ? field.color
            : SignColorCode.WHITE;

      if (field.color !== newColor) {
        const currentText = getValues(`texts.${index}.text`);
        update(index, {
          ...field,
          text: currentText,
          color: newColor,
          fontSize: watch(`texts.${index}.fontSize`),
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryColor]);

  useEffect(() => {
    updateRowNumber(fields, update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  const {
    scaleFactor,
    width: scaledWidth,
    height: scaledHeight,
  } = calculateScaledDimensions(
    width,
    height,
    window.innerWidth < 1700 ? 500 : 800
  );

  const heightCanExpand = signSizeOptions.some((x) => x.height > height);

  return (
    <>
      {fields.map((field, index) => (
        <div key={field.id}>
          <Controller
            name={`texts.${index}.text`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <div>
                <Field
                  label={`${t("label.row")} ${field.row}`}
                  className={styles.label}
                  size="medium"
                >
                  <Input
                    value={value ?? ""}
                    onChange={(e) => {
                      const input = e.target.value;
                      const textWidth = measureTextWidth(
                        input,
                        (watch(`texts.${index}.fontSize`) * scaleFactor) / 0.8,
                        scaledWidth
                      );

                      if (
                        textWidth <=
                        availableTextWidth(
                          width,
                          scaledWidth,
                          scaleFactor,
                          symbol
                        )
                      ) {
                        onChange(input);
                        if (
                          textFieldHasErrorMessage(
                            fieldsWithErrorMessage,
                            index
                          )
                        ) {
                          const updatedFieldsWithInfoText =
                            fieldsWithErrorMessage.filter(
                              (x) => x.field !== `texts.${index}.text`
                            );
                          setFieldsWithErrorMessage(updatedFieldsWithInfoText);
                        }
                      } else {
                        if (
                          !textFieldHasErrorMessage(
                            fieldsWithErrorMessage,
                            index
                          )
                        ) {
                          setFieldsWithErrorMessage((prev) => [
                            ...prev,
                            {
                              field: `texts.${index}.text`,
                              message: t("error.textFieldMaxLength"),
                            },
                          ]);
                        }
                      }
                    }}
                    type="text"
                    className={styles.inputHeight}
                  />
                  {textFieldHasErrorMessage(fieldsWithErrorMessage, index) && (
                    <Text as="i" className={styles.errorText}>
                      {
                        fieldsWithErrorMessage.find(
                          (x) => x.field === `texts.${index}.text`
                        ).message
                      }
                    </Text>
                  )}
                </Field>
              </div>
            )}
          />

          <div style={{ display: "flex", flexDirection: "column" }}>
            <div className={styles.flexCenter}>
              <Controller
                name={`texts.${index}.fontSize`}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Field
                    label={`${t("formSection.size")}`}
                    className={mergeClasses(
                      styles.widthHundred,
                      styles.widthInputMargin,
                      styles.field
                    )}
                    size="medium"
                  >
                    <Dropdown
                      className={styles.inputHeight}
                      value={
                        value
                          ? fontSizeMenuOptions.find((x) => x.value === value)
                              .text ?? ""
                          : ""
                      }
                      onOptionSelect={(e, d) => {
                        onChange(d.optionValue ? Number(d.optionValue) : "");

                        if (
                          textFieldHasErrorMessage(
                            fieldsWithErrorMessage,
                            index
                          )
                        ) {
                          const textWidth = measureTextWidth(
                            watch(`texts.${index}.text`),
                            (Number(d.optionValue) * scaleFactor) / 0.8,
                            scaledWidth
                          );
                          if (
                            textWidth <=
                            availableTextWidth(
                              width,
                              scaledWidth,
                              scaleFactor,
                              symbol
                            )
                          ) {
                            const updatedFieldsWithInfoText =
                              fieldsWithErrorMessage.filter(
                                (x) => x.field !== `texts.${index}.text`
                              );
                            setFieldsWithErrorMessage(
                              updatedFieldsWithInfoText
                            );
                          }
                        }
                      }}
                    >
                      {fontSizeMenuOptions.map((x) => (
                        <Option
                          key={x.key}
                          value={x.value.toString()}
                          disabled={
                            measureTextWidth(
                              watch(`texts.${index}.text`),
                              (x.value * scaleFactor) / 0.8,
                              scaledWidth
                            ) >
                              availableTextWidth(
                                width,
                                scaledWidth,
                                scaleFactor,
                                symbol
                              ) ||
                            !fitsInAvailableTextHeight(
                              x.value,
                              index,
                              height,
                              scaledHeight,
                              scaleFactor,
                              getValues
                            )
                          }
                        >
                          {x.text}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                )}
              />
              <Controller
                name={`texts.${index}.color`}
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <Field
                    label={`${t("formSection.color")}`}
                    className={styles.field}
                    size="medium"
                  >
                    <Dropdown
                      className={styles.inputHeight}
                      value={
                        colorOptions.find(
                          (x) => x.value === fields[index].color
                        )?.text
                      }
                      onOptionSelect={(e, d) => {
                        const selectedColor = d.optionValue as SignColorCode;
                        onChange(selectedColor);
                        const currentText = getValues(`texts.${index}.text`);
                        update(index, {
                          ...fields[index],
                          color: selectedColor,
                          text: currentText,
                        });
                      }}
                    >
                      {colorOptions
                        .filter((x) =>
                          primaryColor === SignColor.YELLOW
                            ? x.key === SignColor.BLACK ||
                              x.key === SignColor.RED
                            : x.key !== SignColor.BLACK
                        )
                        .map((x) => (
                          <Option key={x.key} value={x.value}>
                            {x.text}
                          </Option>
                        ))}
                    </Dropdown>
                  </Field>
                )}
              />
            </div>
            <div className={mergeClasses(styles.flexEnd, styles.btnMargin)}>
              <Button
                size="large"
                className={styles.buttonColor}
                onClick={() => {
                  remove(index);
                }}
              >
                {`${t("button.removeRow")} ${field.row}`}
              </Button>
            </div>
          </div>
        </div>
      ))}
      <Button
        className={styles.button}
        appearance="subtle"
        size="large"
        disabled={
          !canAddTextRow(
            height,
            scaledHeight,
            scaleFactor,
            fields,
            getValues
          ) && !heightCanExpand
        }
        onClick={() => {
          if (
            !canAddTextRow(height, scaledHeight, scaleFactor, fields, getValues)
          ) {
            const canAdd = onSignExpand(
              height,
              width,
              scaledWidth,
              scaleFactor,
              heightCanExpand,
              symbol,
              setValue,
              getValues
            );
            if (!canAdd) {
              return;
            }
          }
          addTextRow(fields, primaryColor, append, getValues);
        }}
      >
        <AddFilled className={styles.icon} />
        {t("button.addRow")}
      </Button>
      <GridItem size="4/12" className={styles.breakRow}>
        <Field
          label={t("Reflex")}
          className={mergeClasses(styles.field, styles.marginTopInModal)}
          size={"large"}
        >
          <RadioGroup
            value={sign.reflex}
            onChange={(_, data) => setValue("reflex" as any, data.value)}
            layout="horizontal"
          >
            <Radio value={Reflex.NORMAL} label={t("Normal")} />
            <Radio value={Reflex.HIGH} label={t("enum.High")} />
          </RadioGroup>
        </Field>
      </GridItem>
    </>
  );
};
